import { AgxButton, AgxSpacer, AgxTooltip } from '@urbanx/agx.ui-components2';

interface TaskModalActionButtonsProps {
  currentPage: number;
  totalPages: number;
  onPrevious: () => void | Promise<void>;
  onNext: () => void | Promise<void>;
  onComplete: () => void | Promise<void>;
  onSaveAndExit: () => void | Promise<void>;
  completedPages: boolean[];
  completeDisabled?: boolean;
  saveAndExitDisabled?: boolean;
  isSaving?: boolean;
  previousButtonText?: string;
}

const TaskModalActionButtons = ({
  currentPage,
  totalPages,
  onPrevious,
  onNext,
  onComplete,
  onSaveAndExit,
  completedPages,
  completeDisabled = false,
  saveAndExitDisabled = false,
  isSaving = false,
  previousButtonText = 'Back',
}: TaskModalActionButtonsProps) => {
  const showPreviousButton = currentPage !== 0;
  const showNextButton = currentPage !== totalPages - 1;
  const showCompleteButton = currentPage === totalPages - 1;
  const incompletePageEntries = completedPages
    .map((isComplete, index) => ({ isComplete, index }))
    .filter(({ isComplete }) => !isComplete);
  const showCompletionTooltip = showCompleteButton && completeDisabled && !isSaving;
  const incompletePageNames = incompletePageEntries.map(({ index }) => `Page ${index + 1}`);
  const incompletePagesList =
    incompletePageNames.length === 1
      ? incompletePageNames[0]
      : incompletePageNames.length === 2
        ? `${incompletePageNames[0]} and ${incompletePageNames[1]}`
        : `${incompletePageNames.slice(0, -1).join(', ')}, and ${incompletePageNames.at(-1)}`;

  const completionTooltip = `Incomplete information on ${incompletePagesList}`;

  return (
    <AgxSpacer
      direction="horizontal"
      fill
      contentJustification="space-between"
      contentAlignment="center"
      gap="xs"
    >
      <AgxButton
        id={'complianceModal-saveAndExit'}
        text={'Save & Exit'}
        variant={'hollow'}
        size="large"
        onClick={onSaveAndExit}
        disabled={saveAndExitDisabled || isSaving}
      />
      <AgxSpacer
        direction="horizontal"
        gap="xs"
        contentJustification="end"
        contentAlignment="center"
      >
        {showPreviousButton && (
          <AgxButton
            id={'complianceModal-previous'}
            text={previousButtonText}
            variant={'hollow'}
            size="large"
            onClick={onPrevious}
            disabled={isSaving}
          />
        )}
        {showNextButton && (
          <AgxButton
            id={'complianceModal-next'}
            text={'Next'}
            variant={'primary'}
            size="large"
            onClick={onNext}
            disabled={isSaving}
          />
        )}
        {showCompleteButton && (
          <AgxTooltip
            id="complete-task"
            content={completionTooltip}
            placement="top"
            disabled={!showCompletionTooltip}
            triggerTabIndex={showCompletionTooltip ? 0 : undefined}
            triggerAriaLabel={
              showCompletionTooltip ? 'Complete unavailable. View missing items.' : undefined
            }
            portaled
          >
            <AgxButton
              id={'complianceModal-complete'}
              text={'Complete'}
              variant={'primary'}
              size="large"
              onClick={onComplete}
              disabled={completeDisabled || isSaving}
            />
          </AgxTooltip>
        )}
      </AgxSpacer>
    </AgxSpacer>
  );
};

export default TaskModalActionButtons;
